'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { ApolloError, OnDataOptions } from '@apollo/client';
import type {
  DownloadJobProgressPayload,
  ImportCompletePayload,
  ImportItemError,
} from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import type { TranscodeKeys } from '@frameio/shared/assets/types';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import type { ImportEventsSubscription } from '@frameio/shared/integrations/gql';
import {
  useCancelImportMutation,
  useImportAssetsMutation,
  useImportEventsSubscription,
} from '@frameio/shared/integrations/gql';
import { useShowFileOnDiskMutation } from '@frameio/shared/integrations/gql/mutations/generated/ShowFileOnDisk';
import { getSourceFromPath } from '@frameio/shared/integrations/utils/trackingUtils';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { formatBytes } from '@frameio/utils/formatters';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogHeader,
  IconButton2,
  ScrollContainer,
  Stack,
  Text,
  theme,
  ToastAlert,
  Tooltip,
  useToast,
} from '@frameio/vapor';
import {
  IconCheckCircle,
  IconError,
  IconInfo,
  IconWarning,
} from '@frameio/vapor-icons';
import { normalizeDownloadItem } from '../utils/importItemUtils';
import TEXT from './generated/TEXT/useImportAssetsToPremiere';
import type { DownloadItem } from './useGetAssetDownloadItems';
import { useGetAssetDownloadItems } from './useGetAssetDownloadItems';

const BYTES_PER_MB = 1_000_000;

/** Used for analytics only. Converts bytes to MB (base-10), rounded to two
 * decimal places. */
function bytesToMB(bytes: number): number {
  return Math.round((bytes / BYTES_PER_MB) * 100) / 100;
}

TEXT.DEFINE(
  {
    errorMessage: 'Error importing assets',
    toastDetails: ({ totalBytesDownloaded = '…', totalBytes = '…' }): string =>
      `${totalBytesDownloaded} of ${totalBytes}`,
    toastTitle: (n: number): string =>
      plural(n, { other: `Importing ${n} Assets`, one: `Importing Asset` }),
    toastTitleComplete: (n: number): string =>
      plural(n, { other: `Imported ${n} Assets`, one: `Imported Asset` }),
    toastTitleCanceled: (n: number): string =>
      plural(n, {
        other: `Import Canceled for ${n} Assets`,
        one: 'Import Canceled',
      }),
    toastTitlePartialComplete: (n: number, total: number): string =>
      plural(n, {
        one: `Import Partially Complete`,
        other: `Import failed for ${n} of ${total} assets`,
      }),
    seeDetails: 'Details',
    detailsDialogTitle: 'Import Details',
    dialogCloseLabel: 'Close Dialog',
    successfulImports: (n: number): string =>
      plural(n, {
        other: `${n} Successful Imports`,
        one: `${n} Successful Import`,
      }),
    failedImports: (n: number): string =>
      plural(n, { other: `${n} Failed Imports`, one: `${n} Failed Import` }),
    showFileOnDisk: 'Show in Finder',
  },
  { plural }
);

type ImportState =
  | { status: 'downloading'; progress: number }
  | { status: 'canceled' }
  | { status: 'complete'; paths: string[]; errors: ImportItemError[] };

function getToastConfig(importState: ImportState, assets: Array<DownloadItem>) {
  switch (importState.status) {
    case 'complete': {
      const hasErrors = importState.errors.length > 0;
      const hasPaths = importState.paths.length > 0;
      const hasDetails = hasErrors || hasPaths;

      // Full success
      if (!hasErrors && hasPaths) {
        return {
          icon: (
            <IconCheckCircle
              color={theme.colors.icon.interactive.success}
              size={24}
            />
          ),
          title: TEXT.toastTitleComplete(assets.length),
          showDetails: false,
          showDetailsButton: hasDetails,
          hasCompleted: true,
        };
      }

      // Partial success
      if (hasErrors && hasPaths) {
        return {
          icon: (
            <IconWarning
              color={theme.colors.icon.interactive.notice}
              size={24}
            />
          ),
          title: TEXT.toastTitlePartialComplete(
            importState.errors.length,
            assets.length
          ) as string,
          showDetails: false,
          showDetailsButton: hasDetails,
          hasCompleted: true,
        };
      }

      // Full error
      if (hasErrors && !hasPaths) {
        return {
          icon: (
            <IconError color={theme.colors.icon.interactive.error} size={24} />
          ),
          title:
            importState.errors.length === 1
              ? importState.errors[0].message
              : TEXT.errorMessage,
          showDetails: false,
          showDetailsButton: hasDetails,
          hasCompleted: true,
        };
      }

      // Fallback (shouldn't happen, but handle it)
      return {
        icon: (
          <IconCheckCircle
            color={theme.colors.icon.interactive.success}
            size={24}
          />
        ),
        title: TEXT.toastTitleComplete(assets.length) as string,
        showDetails: false,
        showDetailsButton: false,
        hasCompleted: true,
      };
    }

    case 'canceled':
      return {
        icon: (
          <IconWarning
            color={theme.colors.icon.interactive.informative}
            size={24}
          />
        ),
        title: TEXT.toastTitleCanceled(assets.length) as string,
        showDetails: false,
        showDetailsButton: false,
        hasCompleted: true,
      };

    case 'downloading':
      return {
        icon: (
          <CircularProgress
            background={false}
            color="informative"
            size={24}
            value={importState.progress * 100}
          />
        ),
        title: TEXT.toastTitle(assets.length),
        showDetails: true,
        showDetailsButton: false,
        hasCompleted: false,
      };
    default:
      return null;
  }
}

function ToastAlertForProgress({
  assets,
  importId,
  onRequestClose,
  closeLabel,
}: {
  assets: Array<DownloadItem>;
  importId: string;
  onRequestClose: ({ hasCompleted }: { hasCompleted: boolean }) => void;
  closeLabel: string;
}) {
  const totalBytes = assets.reduce((acc, asset) => acc + asset.filesize, 0);
  const [importState, setImportState] = useState<ImportState>({
    status: 'downloading',
    progress: 0,
  });
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const isPremiereClient = useIsMatchingClientName('premiere');

  const [showFileOnDisk] = useShowFileOnDiskMutation({
    context: { resolveWithUXP: true },
  });

  const onImportEventsData = useCallback(
    (options: OnDataOptions<ImportEventsSubscription>) => {
      const importEvent = options.data?.data?.importEvents;
      if (!importEvent || ('id' in importEvent && importEvent?.id !== importId))
        return;

      switch (importEvent?.__typename) {
        case 'DownloadJobProgressPayload':
          setImportState({
            status: 'downloading',
            progress: (importEvent as DownloadJobProgressPayload).percentage,
          });
          break;
        case 'ImportCompletePayload':
          setImportState({
            status: 'complete',
            paths: (importEvent as ImportCompletePayload).paths,
            errors: (importEvent as ImportCompletePayload).errors,
          });
          break;
        case 'ImportCanceledPayload':
          setImportState({ status: 'canceled' });
          break;
        default:
          break;
      }
    },
    [importId]
  );

  useImportEventsSubscription({
    context: { resolveWithUXP: true },
    onData: onImportEventsData,
    skip: !isPremiereClient,
  });

  const config = useMemo(
    () => getToastConfig(importState, assets),
    [importState, assets]
  );

  const handleRequestClose = useCallback(() => {
    onRequestClose({ hasCompleted: config?.hasCompleted ?? false });
  }, [onRequestClose, config?.hasCompleted]);

  const paths = importState.status === 'complete' ? importState.paths : [];

  const errors = importState.status === 'complete' ? importState.errors : [];

  const bytesDownloaded =
    importState.status === 'downloading'
      ? importState.progress * totalBytes
      : 0;

  return (
    <>
      <ToastAlert
        adornmentAfter={
          config?.showDetailsButton && (
            <Tooltip title={TEXT.seeDetails}>
              <IconButton2
                icon={<IconInfo size={16} />}
                label={TEXT.seeDetails}
                onClick={() => setShowDetailsDialog(!showDetailsDialog)}
                size="small"
                variant="tertiary"
              />
            </Tooltip>
          )
        }
        adornmentBefore={config?.icon}
        closeLabel={closeLabel}
        details={
          config?.showDetails
            ? TEXT.toastDetails({
                totalBytesDownloaded: formatBytes(bytesDownloaded),
                totalBytes: formatBytes(totalBytes),
              })
            : undefined
        }
        onRequestClose={handleRequestClose}
        title={config?.title ?? ''}
        variant="success"
      />
      <Dialog
        enableClickOutsideToClose={true}
        isShowing={showDetailsDialog}
        onRequestClose={() => setShowDetailsDialog(false)}
        zIndex={theme.zIndices.toast + 1}
      >
        <DialogHeader
          closeLabel={TEXT.dialogCloseLabel}
          title={TEXT.detailsDialogTitle}
        />
        <ScrollContainer
          enableBottomShadow={true}
          enableTopShadow={true}
          maxHeight={'70vh'}
          paddingX={16}
          style={{ maxWidth: '100%' }}
        >
          <Box display="flex" flexDirection="column" gap={16} maxWidth="100%">
            {errors.length > 0 && (
              <div>
                <Text color="notice" variant="body">
                  {TEXT.failedImports(errors.length)}
                </Text>
                <Stack gap={4}>
                  {errors.map((error) => (
                    <Box key={error.path} paddingY={4}>
                      <Text color="primary" variant="caption">
                        {error.message}
                      </Text>
                      <Text color="body" variant="caption">
                        {error.path}
                      </Text>
                    </Box>
                  ))}
                </Stack>
              </div>
            )}
            {paths.length > 0 && (
              <Box display="flex" flexDirection="column" gap={8}>
                <Text color="success" variant="body">
                  {TEXT.successfulImports(paths.length)}
                </Text>
                {paths.map((path) => (
                  <div key={path} style={{ maxWidth: 'calc(100vw - 40px)' }}>
                    <Text
                      color="primary"
                      enableTooltip={true}
                      onClick={() =>
                        showFileOnDisk({ variables: { input: { path } } })
                      }
                      style={{ cursor: 'pointer' }}
                      truncate={3}
                      variant="caption"
                    >
                      {path}
                    </Text>
                  </div>
                ))}
              </Box>
            )}
          </Box>
        </ScrollContainer>
      </Dialog>
    </>
  );
}

type ImportOperation = {
  toastId: string;
  importId: string;
};

export function useImportAssetToPremiere() {
  const activeImports = useRef<Map<string, ImportOperation>>(new Map());
  const { addToast } = useToast();
  const [importAssets] = useImportAssetsMutation({
    context: { resolveWithUXP: true },
  });
  const [cancelImport] = useCancelImportMutation({
    context: { resolveWithUXP: true },
  });
  const track = useTrackUnsafe();
  const pathname = usePathname();

  const { fetchDownloadItems } = useGetAssetDownloadItems();

  const importDownloadItems = useCallback(
    async (assets: Array<DownloadItem>) => {
      const onError = (error: ApolloError) => {
        // eslint-disable-next-line no-console
        console.error(error);
        addToast(
          (closeToast, toastCloseLabel) => (
            <ToastAlert
              closeLabel={toastCloseLabel}
              details={error.message.replaceAll('\n', ' ')}
              onRequestClose={closeToast}
              title={TEXT.errorMessage}
              variant="error"
            />
          ),
          { duration: 1_000 }
        );
      };

      const importResult = await importAssets({
        onError,
        variables: { input: { assets } },
      });

      const importId = importResult.data?.importAssets?.id ?? '';

      if (!importId) {
        return importResult;
      }

      track('import-started', {
        import_source: getSourceFromPath(pathname, 'premiere'),
        item_count: assets.length,
        total_file_size: bytesToMB(
          assets.reduce((acc, asset) => acc + asset.filesize, 0)
        ),
        transfer_job_id: importId,
      });

      const toastId = addToast(
        (closeToast, closeLabel) => (
          <ToastAlertForProgress
            assets={assets}
            closeLabel={closeLabel}
            importId={importId}
            onRequestClose={({ hasCompleted }) => {
              if (!hasCompleted) {
                cancelImport({ variables: { input: { id: importId } } });
              }
              closeToast();
              activeImports.current.delete(importId);
            }}
          />
        ),
        { duration: null }
      );
      activeImports.current.set(importId, { toastId, importId });

      return importResult;
    },
    [cancelImport, addToast, importAssets, track, pathname]
  );

  return {
    importAssetIds: async (
      assetIds: string | string[],
      transcodeKey?: TranscodeKeys
    ) => {
      const items = (await fetchDownloadItems(assetIds, transcodeKey)).map(
        normalizeDownloadItem
      );
      importDownloadItems(items);
    },
  };
}
